import type { Schemas } from '@/lib/api/client';

export type AuditEntry = Schemas['AuditEntryDto'];
export type BackToSuggest = Schemas['RuleBackToSuggest'];

/** The action filter's choices: each groups the API actions an operator looks for together. */
export const AUDIT_KINDS = [
  'publish',
  'link',
  'changes',
  'reject',
  'cancellation',
  'removal',
  'edit',
  'group',
  'undo',
  'source',
  'automation',
  'rules',
  'runs',
] as const;
export type AuditKind = (typeof AUDIT_KINDS)[number];

export const KIND_ACTIONS: Record<AuditKind, string[]> = {
  publish: ['Published'],
  link: ['Linked', 'LinkSet', 'Unlinked'],
  changes: ['ChangesApplied', 'KeptCurrentVersion'],
  reject: ['Rejected'],
  cancellation: ['MarkedCancelled', 'Reinstated'],
  removal: ['Unpublished', 'MarkedStillOn'],
  edit: ['Edited'],
  group: ['DuplicatesResolved'],
  undo: ['Undone'],
  source: [
    'SourceAdded',
    'SourceEdited',
    'SourceEnabled',
    'SourceDisabled',
    'SourcePaused',
    'SourceResumed',
  ],
  automation: ['AutomationPaused', 'AutomationResumed', 'AiModelSwitched'],
  rules: ['RuleAdded', 'RuleEdited', 'RuleModeChanged', 'RuleDeleted'],
  runs: ['RunStarted', 'Imported', 'ReReadRequested'],
};

export const KIND_LABELS: Record<AuditKind, string> = {
  publish: 'Publishing',
  link: 'Linking',
  changes: 'Changed published events',
  reject: 'Rejections',
  cancellation: 'Cancellations',
  removal: 'Removed or kept events',
  edit: 'Edits',
  group: 'Duplicate groups',
  undo: 'Undos',
  source: 'Sources',
  automation: 'Automation and AI model',
  rules: 'Trust rules',
  runs: 'Runs and imports',
};

/** Who made the change, as the design's Actor draws it. The pipeline's filter includes what rules decided; Rules is the rules alone. */
export const ACTOR_OPTIONS = {
  pipeline: { label: 'The pipeline', kinds: ['Pipeline', 'Rule'] },
  rules: { label: 'Rules', kinds: ['Rule'] },
  people: { label: 'People', kinds: ['Person'] },
} as const;
export type ActorFilter = keyof typeof ACTOR_OPTIONS;
export const ACTOR_FILTERS = Object.keys(ACTOR_OPTIONS) as [ActorFilter, ...ActorFilter[]];
export const isActorFilter = (value: string): value is ActorFilter =>
  (ACTOR_FILTERS as readonly string[]).includes(value);

/** The undo's own words: the reverse action, as the design names it. */
export const UNDO_LABELS: Record<string, string> = {
  Published: 'Unpublish',
  Linked: 'Unlink',
  ChangesApplied: 'Undo changes',
  Rejected: 'Restore to pending',
  KeptCurrentVersion: 'Restore to pending',
  Edited: 'Undo edit',
  LinkSet: 'Undo',
  Unlinked: 'Undo',
  DuplicatesResolved: 'Reopen group',
  MarkedCancelled: 'Undo cancelling',
  Reinstated: 'Undo reinstating',
};

/** An undo that takes something out of the app, or rewrites what runners see, confirms first. */
export const reachesTheApp = (entry: AuditEntry): boolean =>
  !!entry.event && (entry.event.created || entry.event.changed);

const nameList = (names: string[]): string => {
  const quoted = names.map((n) => `“${n}”`);
  return quoted.length < 2
    ? (quoted[0] ?? '')
    : `${quoted.slice(0, -1).join(', ')} and ${quoted.at(-1)}`;
};

/** An undo's whole outcome in one message: what it undid, then each rule it moved back to Suggest (null when neither). */
export function undoOutcomeMessage(done: string | null, rules: BackToSuggest[]): string | null {
  if (rules.length === 0) return done;
  const one = rules.length === 1;
  const names = nameList(rules.map((r) => r.ruleName));
  const moved = one
    ? `${names} is back in Suggest because you undid its decision; it won't decide on its own until you choose Act.`
    : `${names} are back in Suggest because you undid their decisions; they won't decide on their own until you choose Act.`;
  return done ? `${done} ${moved}` : moved;
}

/** What undoing a cancellation or reinstatement says, and how a refused one is titled, for the event's name. */
export const CURATION_UNDO = {
  MarkedCancelled: {
    done: (name: string) => `“${name}” is back on.`,
    refused: (name: string) => `Couldn't undo cancelling “${name}”`,
  },
  Reinstated: {
    done: (name: string) => `“${name}” is cancelled again.`,
    refused: (name: string) => `Couldn't undo reinstating “${name}”`,
  },
} as const;

/** What an entry's undo did, said afterwards: "Unpublished “X”; it's back in review." */
export function undoneMessage(entry: AuditEntry): string {
  const record = entry.records[0]?.name;
  const event = entry.event?.name;
  switch (entry.action) {
    case 'Published':
      return `Unpublished “${record ?? event}”. It's back in review.`;
    case 'Linked':
      return `Unlinked “${record}”. It's back in review.`;
    case 'ChangesApplied':
      return `Put back the published version of “${event ?? record}”. The changes are back in review.`;
    case 'Rejected':
      return `“${record}” is back in review.`;
    case 'KeptCurrentVersion':
      return `The changes to “${event ?? record}” are back in review.`;
    case 'Edited':
      return `Undid the edit of “${record}”.`;
    case 'DuplicatesResolved':
      return `Reopened the duplicate group. ${recordsBack(entry.records.length)}`;
    case 'MarkedCancelled':
    case 'Reinstated':
      return CURATION_UNDO[entry.action].done(event ?? '');
    default:
      return `Undid the change to “${record}”.`;
  }
}

export const recordsBack = (n: number): string =>
  n === 0
    ? 'Its records are as they were.'
    : n === 1
      ? '1 record is back in review.'
      : `${n} records are back in review.`;

/** "Couldn't unpublish “X”": a refused undo's title, naming what it would have touched. */
export function undoRefusedTitle(entry: AuditEntry): string {
  const record = `“${entry.records[0]?.name ?? entry.event?.name ?? 'the candidate'}”`;
  switch (entry.action) {
    case 'Published':
      return `Couldn't unpublish ${record}`;
    case 'Linked':
      return `Couldn't unlink ${record}`;
    case 'ChangesApplied':
      return `Couldn't undo the changes to ${record}`;
    case 'Rejected':
    case 'KeptCurrentVersion':
      return `Couldn't restore ${record} to pending`;
    case 'Edited':
      return `Couldn't undo the edit of ${record}`;
    case 'DuplicatesResolved':
      return "Couldn't reopen the duplicate group";
    case 'MarkedCancelled':
    case 'Reinstated':
      return CURATION_UNDO[entry.action].refused(entry.event?.name ?? 'the event');
    default:
      return `Couldn't undo the change to ${record}`;
  }
}
